import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { adminApi } from "@/api/admin";
import { useAuthStore } from "@/auth/store";

export const Route = createFileRoute("/admin/users")({
  component: AdminUsersPage,
});

function AdminUsersPage() {
  const [q, setQ] = useState("");
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-users", q],
    queryFn: () => adminApi.listUsers({ q: q || undefined, limit: 200 }),
  });
  const setRole = useMutation({
    mutationFn: ({ uid, role }: { uid: string; role: "user" | "admin" }) => adminApi.setUserRole(uid, role),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-users"] }),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Users</h1>
        <p className="mt-1 text-ink/65">Search, promote/demote admins, drill into a user's projects.</p>
      </div>

      <input
        className="input"
        placeholder="Search by email…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />

      <div className="card overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="bg-cream text-left text-xs uppercase tracking-wide text-ink/55">
            <tr>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3 text-right">Owns</th>
              <th className="px-4 py-3 text-right">Member of</th>
              <th className="px-4 py-3">Joined</th>
              <th className="px-4 py-3">Last login</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? <tr><td colSpan={7} className="px-4 py-6 text-ink/55">Loading…</td></tr>
            : data?.users.length ? data.users.map((u) => (
              <tr key={u.id} className="border-t border-ink/8 hover:bg-cream/50">
                <td className="px-4 py-3 font-medium">
                  <Link
                    to="/admin/users/$uid"
                    params={{ uid: u.id }}
                    className="text-ink hover:underline"
                  >
                    {u.email}
                  </Link>
                </td>
                <td className="px-4 py-3">
                  {u.role === "admin"
                    ? <span className="rounded bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700">admin</span>
                    : <span className="text-ink/55">user</span>}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {u.owner_projects_count > 0
                    ? <span className="font-medium text-ink">{u.owner_projects_count}</span>
                    : <span className="text-ink/35">0</span>}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {u.member_projects_count > 0
                    ? <span className="font-medium text-ink">{u.member_projects_count}</span>
                    : <span className="text-ink/35">0</span>}
                </td>
                <td className="px-4 py-3 text-ink/55">{new Date(u.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-ink/55">{u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-3 text-right">
                  {u.role === "admin" ? (
                    <button
                      className="text-sm text-red-600 hover:text-red-800 disabled:cursor-not-allowed disabled:text-ink/35"
                      disabled={u.id === me?.id /* can't demote yourself via UI; the API also blocks last admin */}
                      onClick={() => { if (confirm(`Revoke admin from ${u.email}?`)) setRole.mutate({ uid: u.id, role: "user" }); }}
                    >Revoke admin</button>
                  ) : (
                    <button
                      className="text-sm text-primary-600 hover:text-primary-800"
                      onClick={() => { if (confirm(`Grant admin to ${u.email}?`)) setRole.mutate({ uid: u.id, role: "admin" }); }}
                    >Grant admin</button>
                  )}
                </td>
              </tr>
            )) : <tr><td colSpan={7} className="px-4 py-6 text-ink/55">No users.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
